import type { SupplyChainSupplier } from '../hooks/useSupplyChain';
import type { SupplyChainSupplierDocument } from '../types/types';

export type SupplierAnalysisProblem = {
  key:
    | 'missing-documents'
    | 'expired-any-document'
    | 'status-unverified'
    | 'status-flagged'
    | 'status-warning';
  label: string;
};

const endOfTodayTimestamp = () => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return today.getTime();
};

export const isDocumentExpired = (document: SupplyChainSupplierDocument) => {
  if (!document.valid_until) {
    return false;
  }

  const validUntil = new Date(document.valid_until);
  if (Number.isNaN(validUntil.getTime())) {
    return false;
  }

  validUntil.setHours(23, 59, 59, 999);
  return validUntil.getTime() < endOfTodayTimestamp();
};

export const getSupplierAnalysisProblems = (
  supplier: SupplyChainSupplier,
  documents: SupplyChainSupplierDocument[],
): SupplierAnalysisProblem[] => {
  const problems: SupplierAnalysisProblem[] = [];

  if (documents.length === 0) {
    problems.push({
      key: 'missing-documents',
      label: 'missing documents',
    });
  }

  if (documents.some((document) => isDocumentExpired(document))) {
    problems.push({
      key: 'expired-any-document',
      label: 'expired documents',
    });
  }

  if (supplier.compliance_status === 'Unverified') {
    problems.push({
      key: 'status-unverified',
      label: 'unverified',
    });
  }

  if (supplier.compliance_status === 'Flagged') {
    problems.push({
      key: 'status-flagged',
      label: 'flagged',
    });
  }

  if (supplier.compliance_status === 'Warning') {
    problems.push({
      key: 'status-warning',
      label: 'risk warning',
    });
  }

  return problems;
};
